import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ClientDto } from '@client/shared/models/client/client.dto';
import { ClientApiService } from '@client/shared/services/api/client-api.service';

@Injectable({
  providedIn: 'root'
})
export class ClientStateService {

  #clientApiService = inject(ClientApiService);

  readonly #currentClient: WritableSignal<ClientDto | null> = signal(null);
  readonly #isOnboardingMode: WritableSignal<boolean> = signal(false);
  /** Per 403 "therapist-subscription-required" erkannte Sperre; gilt auch ohne geladenen Patienten, bis ein Profil gesetzt wird. */
  readonly #accessLocked: WritableSignal<boolean> = signal(false);

  /** Gesperrt, weil der Haupttherapeut keinen Zugang hat; fehlt das Flag (altes Backend), gilt der Patient als nicht gesperrt. */
  readonly isAccessLocked: Signal<boolean> = computed(() => this.#accessLocked() || this.#currentClient()?.isAccessLocked === true);

  loadClientProfile$(): Observable<ClientDto> {
    return this.#clientApiService.getProfile().pipe(
      tap((data: ClientDto) => {
        this.#currentClient.set(data);
        this.#accessLocked.set(false);
      })
    );
  }

  getCurrentClient(): Signal<ClientDto | null> {
    return this.#currentClient.asReadonly();
  }

  setCurrentClient(client: ClientDto): void {
    this.#currentClient.set(client);
    this.#accessLocked.set(false);
  }

  /** Markiert den Zugang als gesperrt (nach 403 "therapist-subscription-required"), auch ohne geladenen Patienten. */
  markAccessLocked(): void {
    this.#accessLocked.set(true);
  }

  getIsOnboardingMode(): Signal<boolean> {
    return this.#isOnboardingMode.asReadonly();
  }

  setIsOnboardingMode(isOnboardingMode: boolean): void {
    this.#isOnboardingMode.set(isOnboardingMode);
  }

  removeAll(): void {
    this.#currentClient.set(null);
    this.#isOnboardingMode.set(false);
    this.#accessLocked.set(false);
  }
}
